import { Badge, FlexBox, Page, Tabs } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { DixaMessengerPanel } from './components/DixaMessengerCSAT/Panel';
import styles from './satisfaction.module.scss';

const getTab = (
  tabId: string,
  label: string,
  enabled: boolean,
  nudgeUpgrade = false,
) => {
  if (!enabled && !nudgeUpgrade) return null;

  return {
    [tabId]: {
      id: tabId,
      label: (
        <FlexBox gap="100" alignItems="center">
          <span>{label}</span>
          {nudgeUpgrade && !enabled && (
            <Badge
              size="small"
              variant="info"
              className={styles.dixaMessengerBadge}
            >
              Upgrade to access
            </Badge>
          )}
        </FlexBox>
      ),
    },
  };
};

export const Satisfaction = (): JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const flags = useFeatureFlag([
    'has-messenger',
    'csat-for-dixa-messenger',
    'csat-for-email',
    'csat-for-widget-chat',
  ]);

  const tabs = useMemo(
    () => ({
      ...getTab('email', 'Email & Forms', flags.csatForEmail),
      ...getTab('messenger', 'Messenger', flags.csatForDixaMessenger, true),
      ...getTab('phone', 'Phone', true),
      ...getTab('chat', 'Chat', flags.csatForWidgetChat),
    }),
    [flags],
  );

  const tabsToRender = useMemo(
    () =>
      Object.values(tabs).filter(({ id }) =>
        flags.csatForDixaMessenger ? true : id !== DixaMessengerPanel.PAGE,
      ),
    [flags.csatForDixaMessenger, tabs],
  );

  // Get the selected tab from the pathname.
  // TODO: This should eventually be replaced by a better route setup with path params.
  const selectedTab = pathname.match(
    /^\/settings\/satisfaction\/(\w+)(?:\/.*)?$/,
  )?.[1];

  return (
    <Page>
      <Page.Head title="Satisfaction" />

      <Page.Tabs>
        <Tabs
          tabs={tabsToRender}
          selectedId={selectedTab}
          onChange={(tabId: string) =>
            navigate(`/settings/satisfaction/${tabId}`)
          }
        />
      </Page.Tabs>

      <Page.Body>
        <Outlet />
      </Page.Body>
    </Page>
  );
};
